import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Stack,
  Text,
  Group,
  Badge,
  Progress,
  Loader,
  Alert,
  Button,
  ThemeIcon,
  Box,
  TextInput,
  Table,
  ScrollArea,
  ActionIcon,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconRefresh,
  IconTargetArrow,
  IconSearch,
  IconX,
  IconArrowLeft,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";

function ProgramRepsPage() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [programData, setProgramData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (programId) {
      fetchProgramDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const fetchProgramDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint(`/v1/programs/progress/${programId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch program details");
      }

      const result = await response.json();
      setProgramData(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRepDetails = user => {
    navigate(`/downline/programs/${programId}/rep/${user.user_season_id}`, {
      state: {
        programName:
          programData?.assigned_users?.[0]?.program_name || "Program",
        user: {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
        },
        userProgress: {
          user_season_id: user.user_season_id,
          completed_tasks_count: user.completed_tasks_count,
          total_tasks_count: user.total_tasks_count,
          tasks: user.tasks,
        },
      },
    });
  };

  const formatDateTime = dateString => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadge = user => {
    if (user.is_completed) {
      return (
        <Badge color="green" variant="light" size="sm">
          Completed
        </Badge>
      );
    }

    if (user.completed_tasks_count === 0) {
      return (
        <Badge color="gray" variant="light" size="sm">
          Not Started
        </Badge>
      );
    }

    return (
      <Badge color="blue" variant="light" size="sm">
        In Progress
      </Badge>
    );
  };

  const filteredUsers =
    programData?.assigned_users?.filter(user => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email?.toLowerCase() || "";
      const search = searchTerm.toLowerCase();
      return fullName.includes(search) || email.includes(search);
    }) || [];

  // Get last activity date for a user
  const getLastActivity = user => {
    if (!user.tasks || user.tasks.length === 0) return null;

    const completedTasks = user.tasks
      .filter(t => t.is_completed && t.completed_at)
      .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

    return completedTasks.length > 0 ? completedTasks[0].completed_at : null;
  };

  const programName =
    programData?.assigned_users?.[0]?.program_name || "Program Details";

  return (
    <PageContent
      title={
        <Group gap="sm">
          <ThemeIcon size="lg" radius="md" variant="light" color="violet">
            <IconTargetArrow size={20} />
          </ThemeIcon>
          <Box>
            <Text fw={600} size="lg">
              {programName}
            </Text>
            <Text size="sm" c="dimmed">
              Individual Rep Progress
            </Text>
          </Box>
        </Group>
      }
    >
      <Stack gap="lg">
        {/* Back Button and Search */}
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/downline")}
          >
            Back to Programs
          </Button>
          <TextInput
            placeholder="Search reps..."
            leftSection={<IconSearch size={16} />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            rightSection={
              searchTerm && (
                <ActionIcon variant="subtle" onClick={() => setSearchTerm("")}>
                  <IconX size={16} />
                </ActionIcon>
              )
            }
            style={{ minWidth: 300 }}
          />
        </Group>

        {loading ? (
          <Group justify="center" py="xl">
            <Loader size="lg" />
            <Text>Loading program details...</Text>
          </Group>
        ) : error ? (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            <Group justify="space-between">
              <Text>{error}</Text>
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconRefresh size={16} />}
                onClick={fetchProgramDetails}
              >
                Retry
              </Button>
            </Group>
          </Alert>
        ) : programData ? (
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Rep Name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ width: "250px" }}>Progress</Table.Th>
                  <Table.Th>Last Activity</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(user => {
                    const completionPct =
                      user.total_tasks_count > 0
                        ? Math.round(
                            (user.completed_tasks_count /
                              user.total_tasks_count) *
                              100
                          )
                        : 0;
                    const lastActivity = getLastActivity(user);

                    return (
                      <Table.Tr
                        key={user.user_season_id}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewRepDetails(user)}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {user.first_name} {user.last_name}
                          </Text>
                        </Table.Td>
                        <Table.Td>{getStatusBadge(user)}</Table.Td>
                        <Table.Td>
                          <Box>
                            <Group justify="space-between" mb={4}>
                              <Text size="xs" c="dimmed">
                                {user.completed_tasks_count}/
                                {user.total_tasks_count}
                              </Text>
                              <Text size="xs" fw={600}>
                                {completionPct}%
                              </Text>
                            </Group>
                            <Progress
                              value={completionPct}
                              color={
                                user.is_completed
                                  ? "green"
                                  : completionPct >= 50
                                    ? "blue"
                                    : "orange"
                              }
                              size="md"
                            />
                          </Box>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {lastActivity ? formatDateTime(lastActivity) : "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: "center" }}>
                          <Button
                            size="xs"
                            variant="light"
                            onClick={e => {
                              e.stopPropagation();
                              handleViewRepDetails(user);
                            }}
                          >
                            View Details
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text size="sm" c="dimmed" ta="center" py="xl">
                        {searchTerm
                          ? "No reps found matching your search"
                          : "No reps assigned to this program"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        ) : null}
      </Stack>
    </PageContent>
  );
}

export default ProgramRepsPage;
